/**
 * Tracks whether the bundle running in this tab is out of date relative to what the server is
 * now serving, so a long-lived tab (a phone left open overnight across a deploy) does not keep
 * calculating with old code.
 *
 * Two independent signals feed this manager; whichever notices first wins, and the other is
 * harmlessly idempotent:
 *
 *  A. `X-Calculist-Git-Commit` on every API response (AppBuildHeaderMiddleware.cs). Costs no
 *     extra requests and fires the instant the user does anything, which is what catches a
 *     mobile tab whose background timers the OS has frozen.
 *  B. The `/version.json` poll in hooks/useVersionCheck.ts. Kept as its own path so an idle tab
 *     that makes no API calls at all still auto-refreshes.
 *
 * Both compare against `__APP_GIT_COMMIT_SHA__`, the sha baked into THIS bundle at build time
 * (vite.config.ts `define`). Comparing against the running bundle -- rather than against the
 * first value the server happened to return -- is the whole point: the previous implementation
 * seeded its baseline from the first successful fetch, so a tab that made its first check only
 * after a deploy adopted the new sha as its baseline and never reloaded.
 *
 * This is a non-React singleton (same shape as ApiStatusManager) because BaseApiService is not a
 * component and must be able to publish into it.
 */

/** sessionStorage key holding the server sha we last reloaded in order to pick up. */
const RELOAD_ATTEMPT_STORAGE_KEY = 'calc_stale_reload_sha';

function getSessionStorage(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * True only when both shas are known and differ. An empty sha on either side means "cannot
 * tell" -- a local build has no git sha, and an API running without GIT_COMMIT_SHA sends no
 * header -- and must never be reported as stale.
 */
export function isStaleBuild(
    runningSha: string | null | undefined,
    serverSha: string | null | undefined
): boolean {
    const running = (runningSha ?? '').trim();
    const server = (serverSha ?? '').trim();
    if (!running || !server) {
        return false;
    }
    return running !== server;
}

/** Stand-in target used when we are stale by API rejection rather than by an observed sha. */
const FORCED_RELOAD_TARGET = '__forced__';

/**
 * Whether a reload should be suppressed because we already tried one for this exact server sha.
 *
 * Coming back still mismatched against the sha we just reloaded to means the reload did not help
 * (a stale CDN edge, or api and web deployed out of step). Reloading again would loop.
 *
 * @param stamp - the previously stored reload target, or null if we have not reloaded yet
 * @param targetSha - the server sha we would be reloading to now, or null when forced by a 409
 */
export function shouldSuppressReload(stamp: string | null, targetSha: string | null): boolean {
    return stamp !== null && stamp === (targetSha ?? FORCED_RELOAD_TARGET);
}

class AppBuildManager {
    private listeners: Set<(stale: boolean) => void> = new Set();
    private stale = false;
    /** The server sha that made us stale, if known. */
    private serverSha: string | null = null;

    /** The git sha of the bundle running in this tab. Empty for local/dev builds. */
    getRunningSha(): string {
        return (typeof __APP_GIT_COMMIT_SHA__ === 'string' ? __APP_GIT_COMMIT_SHA__ : '').trim();
    }

    isStale(): boolean {
        return this.stale;
    }

    getServerSha(): string | null {
        return this.serverSha;
    }

    /**
     * Subscribe to staleness changes. Immediately notified of the current value, matching
     * ApiStatusManager.subscribe.
     */
    subscribe(callback: (stale: boolean) => void): () => void {
        this.listeners.add(callback);
        callback(this.stale);

        return () => {
            this.listeners.delete(callback);
        };
    }

    /**
     * Record a build sha observed from the server (API response header or version.json).
     * Idempotent: only the transition to stale notifies subscribers.
     *
     * Staleness is deliberately one-way. Once this tab knows it is running old code, nothing the
     * server says later makes that untrue -- only a reload does.
     */
    observeServerSha(sha: string | null | undefined): void {
        if (this.stale) {
            return;
        }
        if (!isStaleBuild(this.getRunningSha(), sha)) {
            return;
        }
        this.serverSha = (sha ?? '').trim();
        this.setStale();
    }

    /**
     * Force the stale state without a sha comparison. Used when the API explicitly rejects a
     * write from an out-of-date client (409 + X-Calculist-Stale-Client).
     */
    markStale(): void {
        if (this.stale) {
            return;
        }
        this.setStale();
    }

    private setStale(): void {
        this.stale = true;
        this.listeners.forEach(callback => callback(true));
    }

    /**
     * Whether a reload is still worth attempting.
     *
     * Guards against a reload loop: we stamp the sha we are reloading in order to pick up, and if
     * we come back still mismatched against that same sha, the reload plainly did not help
     * (a stale CDN edge, or api and web deployed out of step). In that case we stop reloading and
     * leave the banner up so the user is at least told, rather than trapping the tab in a loop.
     */
    canAttemptReload(): boolean {
        let stamp: string | null = null;
        try {
            stamp = getSessionStorage()?.getItem(RELOAD_ATTEMPT_STORAGE_KEY) ?? null;
        } catch {
            stamp = null;
        }
        return !shouldSuppressReload(stamp, this.serverSha);
    }

    /** Stamp the pending reload target, then reload. */
    reloadNow(): void {
        try {
            getSessionStorage()?.setItem(RELOAD_ATTEMPT_STORAGE_KEY, this.serverSha ?? FORCED_RELOAD_TARGET);
        } catch {
            // Storage blocked (private browsing). Reload anyway -- without the stamp we lose the
            // loop breaker, but a working reload matters more than the guard against a rare loop.
        }
        window.location.reload();
    }
}

// Export singleton instance
export const appBuildManager = new AppBuildManager();
